import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Icon } from "../../design-system/primitives/Icon";
import { PageContextBar } from "../navigation/PageContextBar";
import { GlobalSearchPanel } from "../search/GlobalSearchPanel";
import { activatePedEmUpUpdate, requestPedEmUpInstall, subscribeToPedEmUpPwa, type PedEmUpPwaState } from "../../platform/pwa";
import { resolvePublicUrl } from "../../platform/public-url";
import styles from "./AppShell.module.css";

const primary = [
  ["/vaka-ile-ogren", "Vaka ile Öğren"], ["/klinik-kartlar", "Klinik Kartlar"],
  ["/ilac-guvenligi", "İlaç Güvenliği"], ["/egitim-modulleri", "Eğitim Modülleri"],
] as const;

const bottom = [
  ["/", "home", "Ana Sayfa"], ["/vaka-ile-ogren", "case", "Vaka ile Öğren"],
  ["/rehberler", "book", "Rehberler"], ["/ilac-guvenligi", "shield", "İlaç Güvenliği"],
  ["/klinik-kartlar", "list", "Klinik Kartlar"],
] as const;

export function AppShell({ children }: { readonly children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [pwaState, setPwaState] = useState<PedEmUpPwaState>({ canPrompt: false, installed: false, updateAvailable: false });
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false); setLauncherOpen(false); setSearchOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { setSearchOpen(false); setMenuOpen(false); setLauncherOpen(false); }
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, []);

  useEffect(() => subscribeToPedEmUpPwa(setPwaState), []);

  const installApp = async () => {
    const outcome = await requestPedEmUpInstall();
    if (outcome === "manual") setInstallHelpOpen(true);
  };

  useEffect(() => {
    const updateScrollTop = () => setShowScrollTop(window.scrollY > 480);
    updateScrollTop();
    window.addEventListener("scroll", updateScrollTop, { passive: true });
    return () => window.removeEventListener("scroll", updateScrollTop);
  }, []);

  const closeLauncher = () => { setMenuOpen(false); setLauncherOpen(false); };
  const platformUrl = "https://platform.test.pemguide.com/";

  return <div className={styles.app}>
    <a className={styles.skipLink} href="#main-content">İçeriğe geç</a>
    <header className={styles.header}>
      <Link className={styles.brand} to="/" aria-label="PedEmUP ana sayfası">
        <span className={styles.logoSurface}><img src={resolvePublicUrl("/brand/pedemup-logo-transparent.webp")} alt="" width="64" height="64" /></span>
        <span className={styles.wordmark}><strong>PedEmUP</strong><small>by PEMGuide</small></span>
      </Link>
      <nav className={styles.desktopNav} aria-label="Ana navigasyon">
        {primary.map(([to, label]) => <NavLink key={to} to={to}>{label}</NavLink>)}
        <button type="button" aria-expanded={launcherOpen} onClick={() => { setSearchOpen(false); setLauncherOpen((open) => !open); }}><Icon name="grid" /> Tüm içerikler</button>
      </nav>
      <div className={styles.headerActions}>
        {!__PEDEMUP_COMPOSED_BUILD__ && <a className={styles.platformReturn} href={platformUrl} aria-label="PEMGuide Platform'a dön" title="PEMGuide Platform'a dön"><span className={styles.platformReturnIcon} aria-hidden="true">←</span><strong>PEMGuide Platform'a dön</strong><small>Platform</small></a>}
        {!__PEDEMUP_COMPOSED_BUILD__ && !pwaState.installed && <button className={styles.installButton} type="button" aria-label="PedEmUP uygulamasını kur" title="Uygulamayı kur" onClick={() => void installApp()}><Icon name="download" /></button>}
        <button type="button" aria-label={searchOpen ? "Aramayı kapat" : "İçerik ara"} aria-controls="global-search-panel" aria-expanded={searchOpen} onClick={() => { setMenuOpen(false); setLauncherOpen(false); setSearchOpen((open) => !open); }}><Icon name="search" /></button>
        <button className={styles.menuButton} type="button" aria-label={menuOpen ? "Menüyü kapat" : "Menüyü aç"} aria-controls="mobile-launcher" aria-expanded={menuOpen} onClick={() => { setSearchOpen(false); setLauncherOpen(false); setMenuOpen((open) => !open); }}><Icon name={menuOpen ? "close" : "menu"} /></button>
      </div>
      {(launcherOpen || menuOpen) && <Launcher mobile={menuOpen} onClose={closeLauncher} />}
      {searchOpen && <GlobalSearchPanel onClose={() => setSearchOpen(false)} />}
    </header>
    <PageContextBar />
    {!__PEDEMUP_COMPOSED_BUILD__ && pwaState.updateAvailable && !updateDismissed && <aside className={styles.updateNotice} role="alert"><span className={styles.updateIcon} aria-hidden="true">↻</span><span><strong>Yeni PedEmUP sürümü hazır</strong><small>Güncellemeyi şimdi uygulayabilirsiniz.</small></span><div><button className={styles.updatePrimary} type="button" onClick={activatePedEmUpUpdate}>Şimdi güncelle</button><button className={styles.updateDismiss} type="button" aria-label="Güncelleme bildirimini kapat" onClick={() => setUpdateDismissed(true)}>×</button></div></aside>}
    <main id="main-content" tabIndex={-1}>{children}</main>
    <footer className={styles.footer}>
      <div><strong>PedEmUP</strong><p>Çocuk acil ve çocuk yoğun bakım için yapılandırılmış eğitim platformu.</p></div>
      <p>Bu platform eğitim ve klinik karar desteği amacıyla hazırlanmıştır. Tanı ve tedavi sorumluluğu hastayı değerlendiren hekime ve ilgili kurum protokollerine aittir.</p>
    </footer>
    {showScrollTop && <button className={styles.scrollTop} type="button" aria-label="Sayfanın başına dön" title="Başa dön" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}><Icon name="up" /><span>Başa dön</span></button>}
    <nav className={styles.bottomNav} aria-label="Mobil ana navigasyon">
      {bottom.map(([to, iconName, label]) => <NavLink key={to} to={to} end={to === "/"}><Icon name={iconName} /><span>{label}</span></NavLink>)}
    </nav>
    {installHelpOpen && <div className={styles.installLayer} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setInstallHelpOpen(false); }}>
      <section className={styles.installDialog} role="dialog" aria-modal="true" aria-labelledby="install-title">
        <button className={styles.dialogClose} type="button" aria-label="Kurulum penceresini kapat" onClick={() => setInstallHelpOpen(false)}><Icon name="close" /></button>
        <span className={styles.dialogEyebrow}>UYGULAMA KURULUMU</span>
        <h2 id="install-title">PedEmUP’ı cihazınıza ekleyin</h2>
        <img src={resolvePublicUrl("/brand/pedemup-app-icon-v1-192.png")} alt="PedEmUP uygulama ikonu" width="88" height="88" />
        <div className={styles.installSteps}>
          <p><strong>iPhone / iPad:</strong> Safari’de Paylaş’a, ardından “Ana Ekrana Ekle”ye dokunun.</p>
          <p><strong>Mac Safari:</strong> Paylaş menüsünden “Dock’a Ekle”yi seçin.</p>
          <p><strong>Diğer tarayıcılar:</strong> Tarayıcı menüsündeki “Uygulamayı yükle” veya “Uygulama olarak yükle” seçeneğini kullanın.</p>
        </div>
        <button className={styles.dialogDone} type="button" onClick={() => setInstallHelpOpen(false)}>Anladım</button>
      </section>
    </div>}
  </div>;
}

function Launcher({ mobile, onClose }: { readonly mobile: boolean; readonly onClose: () => void }) {
  return <>
    {mobile && <button className={styles.drawerBackdrop} type="button" aria-label="Menüyü kapat" onClick={onClose} />}
    <div id={mobile ? "mobile-launcher" : undefined} className={`${styles.launcher} ${mobile ? styles.mobileLauncher : ""}`}>
      {mobile && <GlobalSearchPanel variant="drawer" onClose={onClose} />}
      <section><span>Hızlı klinik erişim</span><Link to="/klinik-kartlar">Klinik Kartlar</Link><Link to="/nobet-dostu">Nöbet Dostu</Link><Link to="/ilac-guvenligi">İlaç Güvenliği</Link></section>
      <section><span>Öğrenme</span><Link to="/vaka-ile-ogren">Vaka ile Öğren</Link><Link to="/egitim-modulleri">Eğitim Modülleri</Link><Link to="/hata-atlasi">Hata Atlası</Link></section>
      <section><span>Kanıt</span><Link to="/rehberler">Rehberler</Link><Link to="/makale-radar">Makale Radarı</Link><Link to="/makale-kulubu">Makale Kulübü</Link></section>
    </div>
  </>;
}
